<template>
  <section>
    <!-- Conversation -->
    <div
      ref="commentsContainer"
      class="tw-max-h-[460px] tw-min-h-[200px] tw-space-y-4 tw-overflow-y-auto tw-pr-1"
    >
      <!-- Loading State -->
      <div v-if="isLoading" class="tw-space-y-4">
        <div v-for="i in 3" :key="i" class="tw-flex tw-items-end tw-gap-2.5">
          <Skeleton class="tw-h-8 tw-w-8 tw-shrink-0 tw-rounded-full" />
          <div class="tw-space-y-1.5">
            <Skeleton class="tw-h-3 tw-w-24" />
            <Skeleton class="tw-h-12 tw-w-52 tw-rounded-2xl" />
          </div>
        </div>
      </div>

      <!-- Bubbles -->
      <template v-else-if="orderComments.length">
        <div
          v-for="(item, index) in orderComments"
          :key="index"
          class="tw-flex tw-items-end tw-gap-2.5"
          :class="isMine(item) ? 'tw-flex-row-reverse' : 'tw-flex-row'"
        >
          <Avatar size="sm" shape="circle" class="tw-shrink-0">
            <AvatarImage
              v-if="item.author?.profile_picture"
              :src="item.author.profile_picture"
              :alt="item.author?.display_name"
            />
            <AvatarFallback
              class="tw-text-[0.65rem] tw-font-semibold"
              :class="
                isMine(item)
                  ? 'tw-bg-secondary-600 tw-text-white dark:tw-bg-secondary-500'
                  : 'tw-bg-surface-200 tw-text-secondary-700 dark:tw-bg-neutral-700 dark:tw-text-neutral-100'
              "
            >
              {{ getInitials(item.author?.display_name) }}
            </AvatarFallback>
          </Avatar>

          <div
            class="tw-flex tw-max-w-[82%] tw-flex-col"
            :class="isMine(item) ? 'tw-items-end' : 'tw-items-start'"
          >
            <div class="tw-mb-1 tw-flex tw-items-center tw-gap-2 tw-px-1">
              <span
                class="tw-text-xs tw-font-semibold tw-text-secondary-700 dark:tw-text-neutral-300"
              >
                {{ item.author?.display_name }}
              </span>
              <span class="tw-text-[0.7rem] tw-text-surface-400 dark:tw-text-neutral-500">
                {{ moment(item.created_on).format('DD.MM.YYYY') }}
              </span>
            </div>
            <div
              class="tw-whitespace-pre-wrap tw-break-words tw-px-3.5 tw-py-2.5 tw-text-sm tw-leading-relaxed"
              :class="
                isMine(item)
                  ? 'tw-rounded-2xl tw-rounded-br-md tw-bg-secondary-600 tw-text-white dark:tw-bg-secondary-500'
                  : 'tw-rounded-2xl tw-rounded-bl-md tw-bg-surface-100 tw-text-secondary-800 dark:tw-bg-neutral-800 dark:tw-text-neutral-100'
              "
            >
              {{ item.text }}
            </div>
          </div>
        </div>
      </template>

      <!-- Empty State -->
      <div
        v-else
        class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-dashed tw-border-surface-200 tw-py-12 tw-text-center dark:tw-border-neutral-800"
      >
        <MessageSquare class="tw-h-11 tw-w-11 tw-text-surface-300 dark:tw-text-neutral-600" />
        <p class="tw-mt-3 tw-text-sm tw-font-medium tw-text-surface-500 dark:tw-text-neutral-400">
          {{ t('orders.titles.no_comments') }}
        </p>
      </div>
    </div>

    <!-- Post Comment -->
    <div
      class="tw-mt-4 tw-flex tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-surface-200 tw-bg-surface-50 tw-p-2 dark:tw-border-neutral-800 dark:tw-bg-neutral-900/50"
      :title="
        isCommentingDisabled()
          ? t('requests.titles.comments_allowed_in_pending_and_request_clarification_status')
          : ''
      "
    >
      <Input
        v-model="comment"
        :placeholder="t('orders.titles.message_hint')"
        :disabled="isCommentingDisabled()"
        class="tw-flex-1 tw-border-0 tw-bg-transparent tw-shadow-none focus-visible:tw-ring-0"
        :class="{ 'tw-cursor-not-allowed': isCommentingDisabled() }"
        @keypress.enter="addComment()"
      />
      <Button :disabled="isCommentingDisabled() || !comment.trim()" size="sm" @click="addComment()">
        <Send class="tw-h-4 tw-w-4" />
        {{ t('common.forms.send') }}
      </Button>
    </div>
  </section>
</template>

<script setup lang="ts">
import { onMounted, onUpdated, ref, nextTick } from 'vue';
import moment from 'moment';
import { useOrderStore } from '@/stores/order.store';
import { useRouter } from 'vue-router';
import { useRecurringInstructionStore } from '@/stores/recurring-instructions.store';
import { useI18n } from 'vue-i18n';
import { useApiUtils } from '@/_helpers/composables/api-utils.composable';
import { useUserStore } from '@/stores/user.store';
import { storeToRefs } from 'pinia';

// UI Components
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

// Icons
import { Send, MessageSquare } from 'lucide-vue-next';

const getInitials = (name: string | undefined): string => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const { t } = useI18n();
const { handleApiError } = useApiUtils();
const { currentUser } = storeToRefs(useUserStore());

const isMine = (item: any): boolean =>
  item?.author?.id != null && item.author.id === currentUser.value?.id;

const { getOrderComments, createOrderComments } = useOrderStore();
const { getRecurringInstructionComments, createRecurringInstructionComment } =
  useRecurringInstructionStore();

const router = useRouter();

const props = defineProps({
  order: {
    type: Object,
    default: () => ({}),
  },
  recurringInstruction: {
    type: Object,
    default: () => ({}),
  },
});

const orderComments = ref<any[]>([]);
const comment = ref('');
const orderId = ref<number | null>(null);
const isLoading = ref(true);
const commentsContainer = ref<HTMLElement | null>(null);

const scrollToBottom = () => {
  nextTick(() => {
    if (commentsContainer.value) {
      commentsContainer.value.scrollTop = commentsContainer.value.scrollHeight;
    }
  });
};

onUpdated(() => {
  scrollToBottom();
});

const isRecurringInstruction = () => {
  return props.recurringInstruction?.id !== undefined;
};

const getComments = async () => {
  isLoading.value = true;
  try {
    if (isRecurringInstruction()) {
      orderComments.value = await getRecurringInstructionComments(orderId.value!);
    } else {
      const response = await getOrderComments(orderId.value!);
      orderComments.value = response.results;
    }
  } finally {
    isLoading.value = false;
    scrollToBottom();
  }
};

const addComment = async () => {
  if (comment.value.trim() === '') return;

  if (isRecurringInstruction()) {
    await handleApiError(async () => {
      await createRecurringInstructionComment(orderId.value!, comment.value);
      await getComments();
      comment.value = '';
    }, t('requests.titles.successful_comment_added'));
  } else {
    await handleApiError(async () => {
      await createOrderComments({ order: orderId.value, text: comment.value });
      await getComments();
      comment.value = '';
    }, t('requests.titles.successful_comment_added'));
  }
};

const isCommentingDisabled = () => {
  const status: string = props.order?.status || props.recurringInstruction?.status;
  return status === 'DRAFT';
};

onMounted(async () => {
  orderId.value = +router.currentRoute.value.params.id;
  await getComments();
});
</script>
